import {
  useCallback,
  useEffect,
  useMemo,
  FC,
  memo,
  useState,
  startTransition,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SimpleTreeView } from '@mui/x-tree-view';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  skipViewingInvestorTagForAccountsQuery,
  useGetAccountsQuery,
} from '@/store/accountsApi';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { RenderTree } from './AccountsTreeView';

export interface AccountsTabProps {
  initialExcludedAccounts?: number[];
  initialExcludedCategories?: number[];
  setFieldValue: (field: string, value: number[]) => void;
  /**
   * Restricts the options to one client's accounts. Without it an advisor sees
   * their whole book, so on a client-scoped page they could exclude an account
   * that is not that client's — inert on their numbers unless the account is
   * joint, and then it moves the total with nothing on screen explaining it.
   */
  investorId?: number;
  /** When provided, "Clear all" clears every tab's exclusion filters. */
  onClearAllFilters?: () => void;
}

const AccountsTab: FC<AccountsTabProps> = memo(
  ({
    initialExcludedAccounts = [],
    initialExcludedCategories = [],
    setFieldValue,
    onClearAllFilters,
    investorId,
  }) => {
    const { t } = useTranslation();
    const authIsLoaded = useAuthIsLoaded();
    const [isInitialized, setIsInitialized] = useState(false);
    const [excludedAccounts, setExcludedAccounts] = useState<number[]>(
      initialExcludedAccounts,
    );
    const [excludedCategories, setExcludedCategories] = useState<number[]>(
      initialExcludedCategories,
    );

    // Sync local state with Formik values when they change (e.g., from saved filter selection)
    useEffect(() => {
      setExcludedAccounts(initialExcludedAccounts);
    }, [initialExcludedAccounts]);

    useEffect(() => {
      setExcludedCategories(initialExcludedCategories);
    }, [initialExcludedCategories]);

    // Load accounts data internally - component is self-contained
    // Data is stable and never changes, checkbox selections don't affect data loading
    const { data: accountsData, isLoading: accountsLoading } =
      useGetAccountsQuery(
        authIsLoaded
          ? {
              showClosed: false,
              ...skipViewingInvestorTagForAccountsQuery,
              ...(investorId !== undefined ? { InvestorId: investorId } : {}),
            }
          : skipToken,
      );

    // An empty list counts as loaded: scoped to a client with no open accounts
    // the response is legitimately empty, and the spinner below never lifts.
    useEffect(() => {
      if (!isInitialized && accountsData?.Records && !accountsLoading) {
        setIsInitialized(true);
      }
    }, [isInitialized, accountsData, accountsLoading]);

    // Build tree structure - data is stable so this only runs once
    const treeStructure = useMemo(() => {
      if (!accountsData?.Records || accountsData.Records.length === 0) {
        return [];
      }

      // Group accounts by classification
      const categoryMap = new Map<
        number,
        { name: string; accounts: Array<(typeof accountsData.Records)[0]> }
      >();

      accountsData.Records.forEach(
        (account: { ClassificationId: number; ClassificationName: any }) => {
          if (!categoryMap.has(account.ClassificationId)) {
            categoryMap.set(account.ClassificationId, {
              name: account.ClassificationName,
              accounts: [],
            });
          }
          categoryMap.get(account.ClassificationId)!.accounts.push(account);
        },
      );

      // Convert to tree nodes
      return Array.from(categoryMap.entries()).map(
        ([classificationId, category]) => ({
          id: `cat-${classificationId}`,
          name: category.name,
          classificationId,
          accounts: category.accounts.map((account) => ({
            id: `acc-${account.AccountId}`,
            name: account.Name,
            accountId: account.AccountId,
          })),
        }),
      );
    }, [accountsData]);

    // Handle account checkbox toggle - updates local state and global state
    // Checkbox selections don't affect data loading
    const handleAccountChange = useCallback(
      (accountId: number) => {
        setExcludedAccounts((prev) => {
          const isExcluded = prev.includes(accountId);
          const newExcludedAccounts = isExcluded
            ? prev.filter((id) => id !== accountId)
            : [...prev, accountId];

          // Update global state in a transition (non-blocking)
          startTransition(() => {
            setFieldValue('excludedAccounts', newExcludedAccounts);
          });

          return newExcludedAccounts;
        });
      },
      [setFieldValue],
    );

    // Handle category checkbox toggle - controls all children accounts
    // Checkbox selections don't affect data loading
    const handleCategoryToggle = useCallback(
      (classificationId: number, accountIds: number[]) => {
        setExcludedAccounts((prevAccounts) => {
          // Count how many children are currently excluded
          const excludedChildrenCount = accountIds.filter((accountId) =>
            prevAccounts.includes(accountId),
          ).length;

          // Determine action based on current state:
          // - If all children are included (excludedChildrenCount === 0) → exclude all
          // - If all children are excluded (excludedChildrenCount === accountIds.length) → include all
          // - If some children are excluded (indeterminate) → include all (make category checked)
          const shouldExcludeAll = excludedChildrenCount === 0;

          // Calculate new excluded accounts array
          const newExcludedAccounts = shouldExcludeAll
            ? [...prevAccounts, ...accountIds]
            : prevAccounts.filter((id) => !accountIds.includes(id));

          // Update excluded categories accordingly
          setExcludedCategories((prevCategories) => {
            const newExcludedCategories = shouldExcludeAll
              ? [...prevCategories, classificationId]
              : prevCategories.filter((id) => id !== classificationId);

            // Update global state in a transition (non-blocking)
            startTransition(() => {
              setFieldValue('excludedAccounts', newExcludedAccounts);
              setFieldValue('excludedCategories', newExcludedCategories);
            });

            return newExcludedCategories;
          });

          return newExcludedAccounts;
        });
      },
      [setFieldValue],
    );

    // Render tree - data is stable so this only renders once
    // Checkbox selections don't affect data loading or tree structure
    const renderedTree = useMemo(() => {
      if (!treeStructure.length || !isInitialized) {
        return null;
      }

      return (
        <RenderTree
          treeStructure={treeStructure}
          initialFormikValues={{
            excludedAccounts,
            excludedCategories,
          }}
          onAccountToggle={handleAccountChange}
          onCategoryToggle={handleCategoryToggle}
        />
      );
    }, [
      treeStructure,
      isInitialized,
      excludedAccounts,
      excludedCategories,
      handleAccountChange,
      handleCategoryToggle,
    ]);

    // Delay rendering until data is loaded and initialized to prevent flash
    if (!isInitialized) {
      if (!accountsData?.Records || accountsData.Records.length === 0) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        );
      }
      return null;
    }

    // Show warning if no accounts found after initialization
    if (treeStructure.length === 0) {
      return (
        <Paper sx={{ p: 2, maxHeight: '400px', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography
              variant='h6'
              color='warning.main'
            >
              ⚠
            </Typography>
            <Typography
              variant='body1'
              sx={{ ml: 1, color: 'warning.main' }}
            >
              {t('noAccountsFound')}
            </Typography>
          </Box>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            {t('noAccountsFoundDescription')}
          </Typography>
        </Paper>
      );
    }

    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        <SimpleTreeView
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            '& .MuiTreeItem-group': {
              transition: 'none !important',
            },
            '& .MuiCollapse-root': {
              transition: 'none !important',
            },
            '& .MuiCollapse-entered': {
              transition: 'none !important',
            },
          }}
        >
          {renderedTree}
        </SimpleTreeView>

        <Box sx={{ mt: 2, textAlign: 'right', flexShrink: 0 }}>
          <Button
            size='small'
            onClick={() => {
              if (onClearAllFilters) {
                onClearAllFilters();
              } else {
                setExcludedAccounts([]);
                startTransition(() => {
                  setFieldValue('excludedAccounts', []);
                });
              }
            }}
          >
            {t('resetFilters')}
          </Button>
        </Box>
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Re-render when setFieldValue or initial values change
    // This ensures checkboxes reflect excluded filters on mount and when Formik values change
    return (
      prevProps.setFieldValue === nextProps.setFieldValue &&
      prevProps.onClearAllFilters === nextProps.onClearAllFilters &&
      prevProps.initialExcludedAccounts === nextProps.initialExcludedAccounts &&
      prevProps.initialExcludedCategories ===
        nextProps.initialExcludedCategories
    );
  },
);
AccountsTab.displayName = 'AccountsTab';

export default AccountsTab;
